import React, {useEffect, useMemo} from 'react';
import {ErrorProvider, useError} from './Presentation/contexts/ErrorContext';
import UnifiedNotificationContainer from './Presentation/components/Notifications/UnifiedNotificationContainer';
import StandardizedErrorHandler from './Services/common/StandardizedErrorHandler';
import {BrowserRouter, Routes, Route, Navigate} from 'react-router-dom';
import {DeviceInfoProvider} from './Services/device/DeviceInfoContext';
import ProtectedRoute from './Presentation/components/common/ProtectedRoute';
import UnauthorizedScreen from './features/auth/pages/UnauthorizedScreen';
import LoginScreen from './features/auth/pages/LoginScreen';
import FeatureDisabledPage from './features/auth/pages/FeatureDisabledPage';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {useClientProfile} from './core/context/ClientContext';
import {getRoutesForModules} from './core/features/registry';
import {getRouteComponent} from './app/routeComponentMap';

const queryClient = new QueryClient();

/**
 * ErrorContextInitializer component
 * Initializes ErrorContext in StandardizedErrorHandler
 * Must be inside ErrorProvider
 */
const ErrorContextInitializer: React.FC = () => {
  const errorContext = useError();

  useEffect(() => {
    // Initialize ErrorContext in StandardizedErrorHandler
    const handler = StandardizedErrorHandler.getInstance();
    handler.setErrorContext(errorContext);
  }, [errorContext]);

  return null;
};

const App: React.FC = () => {
  const profile = useClientProfile();
  const protectedRoutes = useMemo(() => getRoutesForModules(profile.enabledModules), [profile.enabledModules]);
  const themeClass = profile.theme.designSystem?.themeClass ?? '';

  return (
    <div className={themeClass} data-client={profile.clientId}>
      <ErrorProvider>
        <ErrorContextInitializer />
        <UnifiedNotificationContainer />
        <DeviceInfoProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true
              }}
            >
              <Routes>
                <Route path='/login' element={<LoginScreen />} />
                {protectedRoutes.map(({path, componentKey, permission, permissions, requireServiceProvider}) => {
                  const Component = getRouteComponent(componentKey);
                  if (!Component) return null;
                  return (
                    <Route
                      key={path}
                      path={path}
                      element={
                        <ProtectedRoute permission={permission} permissions={permissions} requireServiceProvider={requireServiceProvider}>
                          <Component />
                        </ProtectedRoute>
                      }
                    />
                  );
                })}
                <Route path='/unauthorized' element={<UnauthorizedScreen />} />
                <Route path='/' element={<Navigate to='/login' replace />} />
                <Route path='*' element={<FeatureDisabledPage />} />
              </Routes>
            </BrowserRouter>
          </QueryClientProvider>
        </DeviceInfoProvider>
      </ErrorProvider>
    </div>
  );
};

export default App;
